// Entrada do checkout público — de onde a pessoa veio e com qual passe.
//
// Extraído do CheckoutFlow (2026-10-20) quando o Reservei ganhou checkout
// próprio: os dois layouts precisam da MESMA leitura do token de handoff, do
// intent salvo e do endereço de volta. Duas cópias disso divergiriam justamente
// na parte que decide identidade e redirecionamento.

import { useLayoutEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  normalizeReserveiReturnPath,
  readReserveiCheckoutIntent,
  saveReserveiCheckoutIntent,
} from '../../lib/public-checkout-api';

export type CheckoutBillingCycle = 'MONTHLY' | 'YEARLY';

export interface CheckoutEntry {
  /** Slug do plano na rota (/checkout/:slug). */
  slug: string | undefined;
  saasOrigin: string | undefined;
  handoffToken: string | undefined;
  /** Destino depois do pagamento (já normalizado quando o SaaS é o Reservei). */
  redirectUrl: string | null;
  initialCycle: CheckoutBillingCycle;
  initialName: string;
  initialEmail: string;
}

/**
 * SaaS de origem, sem efeitos colaterais — serve pra escolher o layout antes de
 * montar o checkout. Token na URL sem `saas` é o Reservei (único emissor hoje).
 */
export function resolveCheckoutOrigin(searchParams: URLSearchParams): string | undefined {
  const routeSaas = searchParams.get('saas');
  if (routeSaas) return routeSaas;
  const queryToken = searchParams.get('handoff_token') ?? searchParams.get('token');
  return queryToken ? 'reservei' : undefined;
}

export function useCheckoutEntry(): CheckoutEntry {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  const persistedIntent = readReserveiCheckoutIntent();
  const routeSaas = searchParams.get('saas');
  const savedIntent =
    routeSaas === 'reservei' && persistedIntent?.planSlug === slug ? persistedIntent : null;
  const queryToken = searchParams.get('handoff_token') ?? searchParams.get('token') ?? undefined;
  const handoffToken = queryToken ?? savedIntent?.handoffToken;
  const saasOrigin = resolveCheckoutOrigin(searchParams);
  const requestedRedirect = searchParams.get('redirect');
  const redirectUrl =
    saasOrigin === 'reservei'
      ? normalizeReserveiReturnPath(requestedRedirect) ?? savedIntent?.redirect ?? null
      : requestedRedirect ?? savedIntent?.redirect ?? null;

  // O token sai da barra de endereço logo na montagem (não fica em histórico,
  // print ou link compartilhado) e vai pro sessionStorage por 30 min — é o que
  // permite recarregar a página sem perder a compra.
  useLayoutEffect(() => {
    if (!queryToken) return;
    saveReserveiCheckoutIntent({
      handoffToken: queryToken,
      planSlug: slug,
      cycle: searchParams.get('cycle') ?? savedIntent?.cycle,
      redirect: searchParams.get('redirect') ?? savedIntent?.redirect,
    });
    const clean = new URL(window.location.href);
    clean.searchParams.delete('token');
    clean.searchParams.delete('handoff_token');
    window.history.replaceState(
      window.history.state,
      '',
      `${clean.pathname}${clean.search}${clean.hash}`,
    );
  }, [queryToken, slug, searchParams, savedIntent?.cycle, savedIntent?.redirect]);

  const cycleParam = (searchParams.get('cycle') ?? savedIntent?.cycle ?? '').toUpperCase();

  return {
    slug,
    saasOrigin,
    handoffToken,
    redirectUrl,
    initialCycle: cycleParam === 'ANUAL' || cycleParam === 'YEARLY' ? 'YEARLY' : 'MONTHLY',
    initialName: searchParams.get('name') ?? '',
    initialEmail: searchParams.get('email') ?? '',
  };
}

/**
 * Para onde o botão de "voltar ao sistema" leva depois do pagamento.
 * Reservei: o caminho de volta é RELATIVO (já normalizado) e é montado sobre a
 * URL configurada do SaaS — nunca sobre uma URL arbitrária vinda da query.
 */
export function resolveSuccessTarget(
  homeUrl: string | null,
  redirectUrl: string | null,
  saasOrigin: string | undefined,
): string | null {
  if (redirectUrl && saasOrigin === 'reservei') {
    try {
      const configuredBase = homeUrl ? new URL(homeUrl) : new URL(window.location.origin);
      const base =
        (configuredBase.protocol === 'https:' || configuredBase.protocol === 'http:') &&
        !configuredBase.username &&
        !configuredBase.password
          ? configuredBase
          : new URL(window.location.origin);
      return new URL(redirectUrl, base).toString();
    } catch {
      // URL configurada inválida não pode quebrar a tela de sucesso.
      return homeUrl;
    }
  }
  return redirectUrl ?? homeUrl;
}
